import * as React from 'react';
import {
    List,
    Datagrid,
    TextField,
    EmailField,
    FunctionField,
    CreateButton,
    EditButton,
    Confirm,
    useNotify,
    useRefresh
} from 'react-admin';
import { Box, Typography, Stack } from '@mui/material';
import { Switch } from '../switch/Switch.tsx';
import { LocationService } from '../../services/location/locationService.ts';
import { IconButton } from '../icon-button/IconButton.tsx';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Button } from 'react-admin';
import { isShabbatOnlyLogin } from '../../core/shabbatOnly.ts';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RestaurantService } from '../../services/restaurant/restaurantService.ts';
import { EmptyBox } from '../empty-box/EmptyBox.tsx';
import { use } from 'i18next';
import * as Sentry from '@sentry/react';

//TODO: move styles to a separate file if possible

const LocationTableActions = () => {
    const { t } = useTranslation();
    const isVendor = localStorage.getItem("ra_role") === "VENDOR";

    return (
        <Box
            sx={{
                paddingTop: 'var(--space-72)',
                paddingBottom: 'var(--space-32)',
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
            }}
        >
            <Typography variant="h5" sx={{
                paddingLeft: 'var(--space-24)',
                fontWeight: 700,
                fontSize: 'var(--text-xxl)',
            }}>
                {isShabbatOnlyLogin() ? t('resources.myVenue', 'My venue') : t('resources.locationsManagement')}
            </Typography>

            {!isVendor &&
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <CreateButton icon={<AddIcon />} label={t('locationsManagement.addLocation').toLocaleUpperCase()} sx={{
                        color: 'var(--color-primary)',
                        fontWeight: 700,
                        fontSize: '16px',
                        backgroundColor: 'transparent',
                        padding: '0',
                        minWidth: '30px',
                        '& .MuiButton-startIcon': { marginRight: 'var(--space-6)' },
                    }} />
                </Box>
            }
        </Box>
    )
};

const LocationStatusToggle = ({ record }: { record: any }) => {
    const { t } = useTranslation();
    const notify = useNotify();
    const refresh = useRefresh();
    const [saving, setSaving] = React.useState(false);
    const isManuallyClosed = !!record.isManuallyClosed;

    const handleToggle = async (_: any, checked: boolean) => {
        // checked === "accepting orders" (online); unchecked === manually closed.
        setSaving(true);
        try {
            await new LocationService().updateManuallyClosedStatus(record.id, !checked);
            notify(t('common.saved'), { type: 'success' });
            refresh();
        } catch (error: any) {
            notify(error?.response?.data?.error ?? t('common.error'), { type: 'error' });
            Sentry.captureException(error);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Stack direction="row" alignItems="center" spacing={1}>
            <Switch
                checked={!isManuallyClosed}
                color="primary"
                size="small"
                disabled={saving}
                onChange={handleToggle}
            />
            <Typography sx={{ fontSize: 'var(--text-xxs)' }}>
                {isManuallyClosed ? t('orders.availability.offline') : t('orders.availability.online')}
            </Typography>
        </Stack>
    );
};

/**
 * Whether customers can see this restaurant at all.
 *
 * Deliberately a separate column from the status toggle above, not merged with
 * it. "Offline" and "Not visible" are easy to confuse at a glance and mean
 * different things: offline still lists the restaurant and shows it as closed,
 * while hidden removes it from the app entirely. One control doing both would
 * leave whoever flipped it unable to say which they meant.
 *
 * Switch on means visible, matching the status toggle where on means accepting
 * orders - so on is the good state in both, rather than one of them inverted.
 */
const LocationVisibilityToggle = ({ record }: { record: any }) => {
    const { t } = useTranslation();
    const notify = useNotify();
    const refresh = useRefresh();
    const [saving, setSaving] = React.useState(false);
    const isHidden = !!record.isHidden;

    const handleToggle = async (_: any, checked: boolean) => {
        // checked === visible to customers; unchecked === hidden.
        setSaving(true);
        try {
            await new LocationService().updateHiddenStatus(record.id, !checked);
            notify(t('common.saved'), { type: 'success' });
            refresh();
        } catch (error: any) {
            notify(error?.response?.data?.error ?? t('common.error'), { type: 'error' });
            Sentry.captureException(error);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Stack direction="row" alignItems="center" spacing={1}>
            <Switch
                checked={!isHidden}
                color="primary"
                size="small"
                disabled={saving}
                onChange={handleToggle}
            />
            <Typography sx={{ fontSize: 'var(--text-xxs)' }}>
                {isHidden
                    ? t('locationsManagement.visibility.hidden')
                    : t('locationsManagement.visibility.visible')}
            </Typography>
        </Stack>
    );
};

export const LocationTable = () => {
    const { t } = useTranslation();
    // A Chabad House sees its venue's details and Edit only: it takes no
    // orders, so no open/closed or visibility switch, no menu, no delete.
    const shabbatOnly = isShabbatOnlyLogin();
    const navigate = useNavigate();
    const [confirmOpen, setConfirmOpen] = React.useState(false);
    const [toDelete, setToDelete] = React.useState<number | null>(null);
    const restaurantService = new RestaurantService();
    const notify = useNotify();
    return (
        <>
            <List
                actions={<LocationTableActions />}
                perPage={25}
                empty={<EmptyBox
                    title={t('locationsManagement.emptyBox.title')}
                    description={t('locationsManagement.emptyBox.description')}
                    resource='locationsManagement'
                />}
                sx={{
                    '& .RaList-main': { px: 4 },
                    '& .RaList-content': {
                        border: '1px solid var(--color-secondary)',
                        borderRadius: 'var(--radius-6)',
                    },
                    '& .RaDatagrid-tbody tr:last-of-type td': {
                        borderBottom: 'none',
                    },
                    '& th.MuiTableCell-root': {
                        paddingLeft: 'var(--space-24)',
                        py: 'var(--space-16)',
                        fontSize: 'var(--text-xs)',
                    },
                    '& td.MuiTableCell-root': {
                        py: 'var(--space-24)',
                        paddingLeft: 'var(--space-24)',
                        fontSize: 'var(--text-xs)',
                    },
                    '& .MuiTablePagination-root': {
                        '& .MuiSelect-select': {
                            paddingRight: '32px !important',
                            paddingLeft: '12px !important',
                        },
                        '& .MuiSelect-icon': {
                            right: '8px',
                        }
                    }
                }}
            >
                <Datagrid
                    bulkActionButtons={false}
                    rowClick={false}
                    sx={{
                        '& .RaDatagrid-headerCell': {
                            fontWeight: 700,
                            letterSpacing: 0.2,
                        },
                    }}
                >
                    <TextField source="id" label={t('locationsManagement.columns.id')} sortable={false} />
                    {!shabbatOnly && <TextField source="locationType" label={t('locationsManagement.columns.locationType')} sortable={false} />}
                    <TextField source="name" label={t('locationsManagement.columns.name')} sortable={false} />
                    <EmailField source="email" label={t('locationsManagement.columns.email')} sortable={false} />
                    <TextField source="phoneNumber" label={t('locationsManagement.columns.phone')} sortable={false} />
                    <TextField source="address" label={t('locationsManagement.columns.address')} sortable={false} />
                    {/* Blank until a city is chosen, which is how an unassigned
                        restaurant makes itself visible rather than quietly
                        dropping out of a city-filtered catalogue. */}
                    <FunctionField
                        sortable={false}
                        label={t('locationsManagement.columns.city')}
                        render={(record: any) => record?.cityName || '—'}
                    />
                    {!shabbatOnly && <FunctionField
                        label={t('locationsManagement.columns.status')}
                        sortable={false}
                        render={record => <LocationStatusToggle record={record} />}
                    />}
                    {!shabbatOnly && <FunctionField
                        label={t('locationsManagement.columns.visibility')}
                        sortable={false}
                        render={record => <LocationVisibilityToggle record={record} />}
                    />}
                    <FunctionField
                        label=""
                        sortable={false}
                        render={record => (
                            <>
                                <EditButton
                                    record={record}
                                    resource="locationsManagement"
                                    icon={false}
                                    label={t('locationsManagement.editLocation')}
                                    sx={{
                                        fontSize: 'var(--text-xs)',
                                        backgroundColor: 'transparent',
                                        color: 'var(--color-primary)',
                                        fontWeight: '700',
                                        textTransform: 'none',
                                        padding: 0,
                                        minWidth: 'auto'
                                    }}
                                />

                                {!shabbatOnly && <Button
                                    label={t('locationsManagement.items')}
                                    style={{
                                        fontSize: 'var(--text-xs)',
                                        backgroundColor: 'transparent',
                                        color: 'var(--color-primary)',
                                        fontWeight: '700'
                                    }}
                                    onClick={() =>
                                        navigate(`/locationsManagement/${record.id}/items`, {
                                            state: { locationId: record.id, locationName: record.name },
                                        })
                                    }
                                />}

                                {!shabbatOnly && <IconButton
                                    icon={<DeleteOutlineIcon />}
                                    color="var(--color-text-primary)"
                                    onClick={() => {
                                        setConfirmOpen(true);
                                        setToDelete(record.id);
                                    }}
                                />}
                            </>
                        )}
                    />
                </Datagrid>
            </List>
            <Confirm
                isOpen={confirmOpen}
                title={t('commissionManagement.confirmDeletion.confirmDeletionTitle',)}
                content={t('commissionManagement.confirmDeletion.confirmDeletionBody')}
                confirm={t('common.delete')}
                cancel={t('common.cancel')}
                onConfirm={async () => {
                    try {
                        await restaurantService.deleteAgency(toDelete!)
                        notify(t('common.saved'), { type: 'success' });
                        setConfirmOpen(false);
                        setToDelete(null);
                    }
                    catch (error) {
                        notify(error.response.data.error, { type: 'error' });
                        console.error(error.response.data.error);
                        Sentry.captureException(error);
                        setConfirmOpen(false);
                        setToDelete(null);
                    }
                }}
                onClose={() => {
                    setConfirmOpen(false);
                    setToDelete(null);
                }}
            />
        </>
    )
};
